import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, User, GlassWater, Shield, Music, Search, FileText, Theater, Megaphone, Disc } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

export const StaffPage = () => {
  const mockEmployees = [
    { id: 1, name: "Alex Johnson", role: "Bartender", email: "alex@example.com", phone: "(555) 123-4567", certified: true },
    { id: 2, name: "Sam Wilson", role: "Security", email: "sam@example.com", phone: "(555) 234-5678", certified: true },
    { id: 3, name: "DJ Max", role: "DJ", email: "max@example.com", phone: "(555) 345-6789", certified: false },
    { id: 4, name: "Jamie Smith", role: "Bartender", email: "jamie@example.com", phone: "(555) 456-7890", certified: true },
    { id: 5, name: "Casey Taylor", role: "Security", email: "casey@example.com", phone: "(555) 567-8901", certified: true },
    { id: 6, name: "Sophia Lee", role: "Performer", email: "sophia@example.com", phone: "(555) 678-9012", certified: false },
    { id: 7, name: "Mike Rodriguez", role: "Promoter", email: "mike@example.com", phone: "(555) 789-0123", certified: false },
    { id: 8, name: "Taylor Blake", role: "Promoter", email: "taylor@example.com", phone: "(555) 890-1234", certified: true },
  ];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Bartender':
        return <GlassWater className="h-5 w-5 text-teal-500" />;
      case 'Security':
        return <Shield className="h-5 w-5 text-blue-500" />;
      case 'DJ':
        return <Disc className="h-5 w-5 text-purple-500" />;
      case 'Performer':
        return <Theater className="h-5 w-5 text-pink-500" />;
      case 'Promoter':
        return <Megaphone className="h-5 w-5 text-amber-500" />;
      default:
        return <User className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 md:mb-8 space-y-4 sm:space-y-0">
        <h1 className="text-2xl md:text-3xl font-bold">Staff Management</h1>
        <Button className="self-start sm:self-auto">
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Employee
        </Button>
      </div>

      <div className="mb-6 flex">
        <div className="relative flex-1 max-w-full sm:max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search employees..."
            className="pl-8"
          />
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="all" className="whitespace-nowrap">All</TabsTrigger>
          <TabsTrigger value="bartenders" className="whitespace-nowrap">Bartenders</TabsTrigger>
          <TabsTrigger value="security" className="whitespace-nowrap">Security</TabsTrigger>
          <TabsTrigger value="djs" className="whitespace-nowrap">DJs</TabsTrigger>
          <TabsTrigger value="performers" className="whitespace-nowrap">Performers</TabsTrigger>
          <TabsTrigger value="promoters" className="whitespace-nowrap">Promoters</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Employee Directory</CardTitle>
              <CardDescription>Manage your venue employees</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees.map((employee) => (
                  <div key={employee.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg space-y-3 sm:space-y-0">
                    <div className="flex items-center gap-4">
                      <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full flex-shrink-0">
                        {getRoleIcon(employee.role)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold truncate">{employee.name}</h3>
                        <p className="text-sm text-muted-foreground">{employee.role}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                          <span className="truncate">{employee.email}</span>
                          <span className="hidden sm:inline">•</span>
                          <span>{employee.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      {employee.certified && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                          Certified
                        </Badge>
                      )}
                      <Button variant="outline" size="sm">View</Button>
                      <Button variant="ghost" size="sm">Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bartenders" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GlassWater className="h-5 w-5 text-teal-500" />
                Bartenders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Bartender')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <GlassWater className="h-5 w-5 text-teal-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {employee.certified && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            RSA Certified
                          </Badge>
                        )}
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" />
                Security Staff
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Security')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Shield className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {employee.certified && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            Security Licensed
                          </Badge>
                        )}
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="djs" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Disc className="h-5 w-5 text-purple-500" />
                DJs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'DJ')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Disc className="h-5 w-5 text-purple-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performers" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Theater className="h-5 w-5 text-pink-500" />
                Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Performer')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Theater className="h-5 w-5 text-pink-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promoters" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-amber-500" />
                Promoters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockEmployees
                  .filter(employee => employee.role === 'Promoter')
                  .map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full">
                          <Megaphone className="h-5 w-5 text-amber-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{employee.name}</h3>
                          <div className="flex items-center gap-2 text-sm">
                            <span>{employee.email}</span>
                            <span>•</span>
                            <span>{employee.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {employee.certified && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            Certified
                          </Badge>
                        )}
                        <Button variant="outline" size="sm">View</Button>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Staff Documents
          </CardTitle>
          <CardDescription>
            Manage schedules, contracts, and certifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg space-y-2 sm:space-y-0">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <span className="font-medium">Weekly Staff Schedule</span>
              </div>
              <Button variant="outline" size="sm" className="self-end sm:self-auto">View</Button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg space-y-2 sm:space-y-0">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <span className="font-medium">Training Records</span>
              </div>
              <Button variant="outline" size="sm" className="self-end sm:self-auto">View</Button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg space-y-2 sm:space-y-0">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <span className="font-medium">Certification Tracker</span>
              </div>
              <Button variant="outline" size="sm" className="self-end sm:self-auto">View</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
